// Servicio para manejar las operaciones relacionadas con los precios de venta

import db from "../db/database";
import { fetchFromBusinessCentralEspecial } from "../utils/businessCentralUtils";
import { isOnline } from "./syncService";
import { getClientByNo } from "./clientService";
import { COUNTRY_CODES } from "../constants/countries";

// Función para guardar precios de venta en la base de datos local
export const saveSalesPricesToLocalDB = async (salesPrices) => {
    try {
        const pricesToAdd = salesPrices.map(price => ({
            Item_No: price.Item_No,
            Sales_Type: price.Sales_Type,
            Sales_Code: price.Sales_Code,
            Minimum_Quantity: price.Minimum_Quantity,
            Unit_of_Measure_Code: price.Unit_of_Measure_Code,
            Starting_Date: price.Starting_Date,
            Ending_Date: price.Ending_Date,
            Unit_Price: price.Unit_Price,
            Currency_Code: price.Currency_Code,
            Variant_Code: price.Variant_Code,
            Source_Counter: price.Source_Counter,
            pendingSync: false,
            lastModified: new Date(),
            lastSynced: new Date(),
            deleted: false
        }));

        await db.salesPrices.bulkAdd(pricesToAdd);
    } catch (error) {
        console.error("Error al guardar precios de venta en la base de datos local:", error);
        throw error;
    }
};

// Obtener todos los precios de venta - prioriza servidor si hay internet
export const getAllSalesPrices = async () => {
    try {
        // Obtener precios locales primero
        const localPrices = await db.salesPrices
            .filter((price) => !price.deleted)
            .toArray();

        // Si hay internet, intentar obtener desde Business Central
        if (isOnline()) {
            try {
                let lastSourceCode = null;

                if (localPrices.length > 0) {
                    lastSourceCode = localPrices.sort((a, b) => a.Source_Counter - b.Source_Counter)[localPrices.length - 1].Source_Counter;
                }

                console.log("lastSourceCode", lastSourceCode);

                const response = await fetchFromBusinessCentralEspecial("Importaciones_getcurrentsalesprices", {
                    data: JSON.stringify({
                        Source_Counter: lastSourceCode ? lastSourceCode : 0
                    })
                });

                if (!lastSourceCode) {
                    await db.salesPrices.clear();
                    // Guardar todos los precios del servidor
                    await saveSalesPricesToLocalDB(JSON.parse(response).SalesPrices);
                } else {
                    // Procesar respuesta del servidor
                    if (response) {
                        const serverPrices = JSON.parse(response).SalesPrices;

                        console.log("parseResponse");

                        if (serverPrices && serverPrices.length > 0) {
                            // Crear mapa de precios locales para búsqueda rápida
                            const localPricesByKey = {};
                            localPrices.forEach(price => {
                                const key = `${price.Item_No}-${price.Sales_Type}-${price.Sales_Code}`;
                                localPricesByKey[key] = price;
                            });

                            console.log("localPricesByKey");

                            // Preparar datos para operaciones en bloque
                            const pricesToUpdate = [];
                            const pricesToAdd = [];

                            for (const serverPrice of serverPrices) {
                                const key = `${serverPrice.Item_No}-${serverPrice.Sales_Type}-${serverPrice.Sales_Code}`;
                                const localPrice = localPricesByKey[key];

                                if (localPrice) {
                                    // Preparar actualización si no está pendiente de sincronización
                                    if (!localPrice.pendingSync) {
                                        pricesToUpdate.push({
                                            key: localPrice.id,
                                            changes: {
                                                ...serverPrice,
                                                Source_Counter: serverPrice.Source_Counter,
                                                lastModified: new Date(),
                                                lastSynced: new Date(),
                                                pendingSync: false,
                                                deleted: false
                                            }
                                        });
                                    }
                                } else {
                                    // Preparar inserción de nuevo precio
                                    pricesToAdd.push({
                                        Item_No: serverPrice.Item_No,
                                        Sales_Type: serverPrice.Sales_Type,
                                        Sales_Code: serverPrice.Sales_Code,
                                        Minimum_Quantity: serverPrice.Minimum_Quantity,
                                        Unit_of_Measure_Code: serverPrice.Unit_of_Measure_Code,
                                        Starting_Date: serverPrice.Starting_Date,
                                        Ending_Date: serverPrice.Ending_Date,
                                        Unit_Price: serverPrice.Unit_Price,
                                        Currency_Code: serverPrice.Currency_Code,
                                        Variant_Code: serverPrice.Variant_Code,
                                        Source_Counter: serverPrice.Source_Counter,
                                        lastSynced: new Date(),
                                        pendingSync: false,
                                        deleted: false
                                    });
                                }
                            }

                            // Ejecutar operaciones en bloque
                            if (pricesToUpdate.length > 0) {
                                await db.transaction('rw', db.salesPrices, async () => {
                                    for (const update of pricesToUpdate) {
                                        await db.salesPrices.update(update.key, update.changes);
                                    }
                                });
                            }

                            if (pricesToAdd.length > 0) {
                                await db.salesPrices.bulkAdd(pricesToAdd);
                            }

                            console.log("updatedPrices");

                            // Obtener todos los precios actualizados
                            const updatedPrices = await db.salesPrices
                                .filter((price) => !price.deleted)
                                .toArray();

                            console.log(`Obtenidos ${updatedPrices.length} precios de venta (${serverPrices.length} del servidor)`);
                            return updatedPrices;
                        }
                    }
                }
            } catch (serverError) {
                console.warn("Error al obtener precios del servidor, usando datos locales:", serverError);
                // Si falla el servidor, continuar con datos locales
            }
        } else {
            console.log("Sin conexión a internet, usando precios de venta locales");
        }

        // Fallback: usar precios locales
        if (localPrices.length > 0) {
            console.log(`Obtenidos ${localPrices.length} precios de venta desde la base de datos local`);
            return localPrices;
        }

        // Si no hay precios locales ni del servidor, devolver array vacío
        console.log("No hay precios de venta disponibles");
        return [];

    } catch (error) {
        console.error("Error al obtener precios de venta:", error);
        throw error;
    }
};

// Obtener precio de venta por Item_No desde la base de datos local
export const getSalesPriceByItemNo = async (itemNo) => {
    try {
        // Primero intentar obtener todos los precios (esto manejará la sincronización)
        await getAllSalesPrices();

        // Luego buscar en la base de datos local
        const filteredPrices = await db.salesPrices
            .where("Item_No")
            .equals(itemNo)
            .and((price) => !price.deleted)
            .toArray();

        return filteredPrices;
    } catch (error) {
        console.error(`Error al obtener precio de venta para item ${itemNo}:`, error);
        throw error;
    }
};

// Obtener precios de venta por tipo de venta desde la base de datos local
export const getSalesPricesBySalesType = async (salesType) => {
    try {
        // Primero intentar obtener todos los precios (esto manejará la sincronización)
        await getAllSalesPrices();

        // Luego buscar en la base de datos local
        const filteredPrices = await db.salesPrices
            .where("Sales_Type")
            .equals(salesType)
            .and((price) => !price.deleted)
            .toArray();

        return filteredPrices;
    } catch (error) {
        console.error(`Error al obtener precios de venta para tipo ${salesType}:`, error);
        throw error;
    }
};

// Obtener precios de venta por código de moneda desde la base de datos local
export const getSalesPricesByCurrency = async (currencyCode) => {
    try {
        // Primero intentar obtener todos los precios (esto manejará la sincronización)
        await getAllSalesPrices();

        // Luego buscar en la base de datos local
        const filteredPrices = await db.salesPrices
            .where("Currency_Code")
            .equals(currencyCode)
            .and((price) => !price.deleted)
            .toArray();

        return filteredPrices;
    } catch (error) {
        console.error(`Error al obtener precios de venta para moneda ${currencyCode}:`, error);
        throw error;
    }
};

//Reglas para obtener precio de ventas
const getPriceEcuador = async (allPrices, itemNo, clientNo, customerPriceGroup, colegioNo) => {
    // Orden de prioridades :
    // 1. Colegio
    // 2. Cliente
    // 3. Grupo precio cliente 
    // 4. Todos los clientes

    // Prioridad 1: Buscar precio por colegio
    if (colegioNo) {
        const colegioPrice = allPrices.find(price =>
            price.Sales_Type === "Colegio" && price.Sales_Code === colegioNo
        );
        if (colegioPrice) {
            console.log(`Precio encontrado por colegio ${colegioNo} para producto ${itemNo}:`, colegioPrice.Unit_Price);
            return colegioPrice;
        }
    }

    // Prioridad 2: Buscar precio por cliente específico
    if (clientNo) {
        const clientPrice = allPrices.find(price =>
            price.Sales_Type === "Cliente" && price.Sales_Code === clientNo
        );
        if (clientPrice) {
            console.log(`Precio encontrado por cliente ${clientNo} para producto ${itemNo}:`, clientPrice.Unit_Price);
            return clientPrice;
        }
    }

    // Prioridad 3: Buscar precio por grupo de precio del cliente
    if (customerPriceGroup) {
        const groupPrice = allPrices.find(price =>
            price.Sales_Type === "Grupo precio cliente" && price.Sales_Code === customerPriceGroup
        );
        if (groupPrice) {
            console.log(`Precio encontrado por grupo ${customerPriceGroup} para producto ${itemNo}:`, groupPrice.Unit_Price);
            return groupPrice;
        }
    }

    // Prioridad 4: Buscar precio para todos los clientes
    const allClientsPrice = allPrices.find(price =>
        price.Sales_Type === "Todos los clientes" ||
        price.Sales_Type === "All Customers" ||
        (!price.Sales_Type || price.Sales_Type === "")
    );
    if (allClientsPrice) {
        console.log(`Precio encontrado para todos los clientes para producto ${itemNo}:`, allClientsPrice.Unit_Price);
        return allClientsPrice;
    }

    // Si no se encuentra ningún precio con las prioridades, devolver el primer precio válido
    console.warn(`No se encontró precio específico para producto ${itemNo}, usando precio genérico`);
    return allPrices[0];
}

const getPriceParaguay = async (allPrices, itemNo, clientNo, customerPriceGroup) => {
    if (clientNo) {
        const clientPrice = allPrices.find(price =>
            price.Sales_Type === "Cliente" && price.Sales_Code === clientNo
        );
        if (clientPrice) {
            console.log(`Precio encontrado por cliente ${clientNo} para producto ${itemNo}:`, clientPrice.Unit_Price);
            return clientPrice;
        }
    }

    if (customerPriceGroup) {
        const groupPrice = allPrices.find(price =>
            (price.Sales_Type === "Grupo precio cliente" || price.Sales_Type === "Customer Price Group") && price.Sales_Code === customerPriceGroup
        );
        if (groupPrice) {
            console.log(`Precio encontrado por grupo ${customerPriceGroup} para producto ${itemNo}:`, groupPrice.Unit_Price);
            return groupPrice;
        }
    }

    const allClientsPrice = allPrices.find(price =>
        price.Sales_Type === "Todos los clientes" ||
        price.Sales_Type === "All Customers" ||
        (!price.Sales_Type || price.Sales_Type === "")
    );

    if (allClientsPrice) {
        console.log(`Precio encontrado para todos los clientes para producto ${itemNo}:`, allClientsPrice.Unit_Price);
        return allClientsPrice;
    }

    return null;
}

// Obtener precio de venta con prioridades por cliente, colegio, grupo y producto
export const getSalesPriceWithPriority = async (itemNo, clientNo, customerPriceGroup, colegioNo, country) => {
    try {
        // Obtener todos los precios de venta para el producto desde la base de datos local
        const allPrices = await getSalesPriceByItemNo(itemNo);

        if (!allPrices || allPrices.length === 0) {
            console.warn(`No se encontraron precios para el producto ${itemNo}`);
            return null;
        }

        if (country?.code === COUNTRY_CODES.ECUADOR || COUNTRY_CODES.HONDURAS) {
            return await getPriceEcuador(allPrices, itemNo, clientNo, customerPriceGroup, colegioNo);
        }
        if (country?.code === COUNTRY_CODES.PARAGUAY) {
            return await getPriceParaguay(allPrices, itemNo, clientNo, customerPriceGroup);
        }

    } catch (error) {
        console.error(`Error al obtener precio con prioridades para producto ${itemNo}:`, error);
        throw error;
    }
};